import { useLayoutEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useActivation } from '@/hooks/view-model-hooks'
import { useFeatures, useRootStore } from '@/contexts/root-store-context'
import { links, parseServiceTypeFromParams } from '@/utils/link'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { matchPath, useLocation, useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { JobsContainer } from '@/features/dashboard/components/Jobs/JobsContainer'
import { useTranslate } from '@/hooks/translate'
import { useObservable, useSubject } from '@/hooks/rx-hooks'
import { ServiceTypesDialogContainer } from '@/features/dashboard/components/ServiceTypesDialog/ServiceTypesDialogContainer'
import { ServiceTypesContainer } from '@/features/dashboard/components/ServiceTypes/ServiceTypesContainer'
import { LegendsDialogContainer } from '@/features/dashboard/components/LegendsDialog/LegendsDialogContainer'
import { type ServiceSelectionState } from '@/features/dashboard/DashboardView/ServiceSelectionViewModel'
import { toStream } from '@/utils/mobx-util'
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  ignoreElements,
  merge,
  switchMap,
  withLatestFrom,
} from 'rxjs'
import { unpair } from '@/utils/rx-util'
import { map, share, take, tap } from 'rxjs/operators'
import type { DashboardViewModel } from '@/features/dashboard/DashboardView/DashboardViewModel'
import type { Id } from '@/infra/id'
import { useEffectEvent } from '@react-aria/utils'
import { ViewAllJobsTip } from '@/features/dashboard/components/ViewAllJobsTip'
import { useDocumentTitle } from '@/hooks/document-title'
import { Logo } from '@/features/dashboard/components/Logo'
import { DeadlinesContainer } from '@/features/dashboard/components/Deadlines'
import { Footer } from '@/features/dashboard/components/Footer'
import {
  Description,
  Page,
  PageLayout,
  Separator,
} from '@/features/dashboard/components/PageLayout'
import {
  PageHeader,
  ReturnButton,
  StartNewServiceButton,
} from '@/features/dashboard/components/PageHeader'
import { MissingJobCreatePermissionEmptyState } from '@/features/dashboard/components/Jobs'
import { CallAssistanceDialogsContainer } from '@/features/dashboard/components/CallAssistanceDialogs'
import { ServiceTypeInfoDialogContainer } from '@/features/dashboard/components/ServiceTypeInfoDialog/ServiceTypeInfoDialogContainer'
import { StartNewServiceConfirmationDialogContainer } from '@/features/dashboard/components/StartNewServiceConfirmationDialog/StartNewServiceConfirmationDialogContainer'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { useRootExitMethod } from '@/features/shell/exiting/exit-method-hooks'
import { AnnouncementBannerContainer } from '@/features/dashboard/DashboardView/AnnouncementBannerContainer'
import { PageTopOffsetContext } from '@/contexts/page-top-offset-context'
import { useElementSize } from '@/hooks/element-hooks'

/**
 * The deadlines for the dashboard.
 */
const deadlines = [
  {
    date: { year: 2026, month: 3, day: 16 },
    description: 'S corporations and partnerships filing deadline.',
  },
  {
    date: { year: 2026, month: 4, day: 15 },
    description:
      'C corporations, sole proprietorships, and individual tax returns filing deadline.',
  },
  {
    date: { year: 2026, month: 9, day: 15 },
    description:
      'Extended filing deadline for S corporations and partnerships.',
  },
  {
    date: { year: 2026, month: 10, day: 15 },
    description: 'Extended filing deadline for C corporations and individuals.',
  },
]

export default observer(function DashboardViewContainer() {
  useDocumentTitle('Dashboard')

  const isSpacious = useIsSpacious()

  const { internal } = useFeatures(({ dashboard }) => ({
    internal: dashboard.internal(),
  }))
  const { dashboardVm } = internal

  useActivation(dashboardVm)
  useSyncUrlWithSelectionState(dashboardVm)

  const {
    shell: { shellInfo },
  } = useRootStore()

  const T = useTranslate()
  const canCreateJob = dashboardVm.member.permissions.startNewJob
  const lightDark = useLightDarkFn()

  const { exitMethodType, exitMethodLabel, exitMethodProps } =
    useRootExitMethod()

  const { connectRef: connectAnnouncementRef, size: announcementSize } =
    useElementSize()

  return (
    <PageTopOffsetContext value={announcementSize.height}>
      <AnnouncementBannerContainer ref={connectAnnouncementRef} />
      <PageHeader
        title={`Welcome, ${dashboardVm.member.user.displayName}`}
        backButton={
          exitMethodType === 'ReturnToPartner' ? (
            <ReturnButton label={exitMethodLabel} {...exitMethodProps} />
          ) : undefined
        }
        action={
          !canCreateJob ? undefined : !dashboardVm.isCompletelyEmpty ? (
            <StartNewServiceButton
              testId="start-new-button"
              isSkeleton={dashboardVm.isShowingLoadingState}
              onPress={() => dashboardVm.requestService()}
              isPending={dashboardVm.serviceSelectionVm.isPending}
              isDisabled={
                dashboardVm.isBlockingInteractionsDueToLackOfTermsAcceptance
              }
              label={T('Web.Dashboard.Button.New', 'Start new service')}
              compactLabel={T('Web.Dashboard.Button.New.Compact', 'Start new')}
            />
          ) : undefined
        }
      >
        <Page testId="dashboard-page">
          <PageLayout
            asideHeader={
              <>
                <Logo
                  to={links.home()}
                  src={lightDark(shellInfo.logoUrl, shellInfo.invertedLogoUrl)}
                  alt={shellInfo.name}
                />
                <Description>
                  {dashboardVm.isCompletelyEmpty
                    ? T(
                        'Web.Dashboard.WelcomeMessage.Empty',
                        'Your taxes, handled. Pick a service to get started.',
                      )
                    : T(
                        'Web.Dashboard.WelcomeMessage',
                        'Accounting made easy. Start now or pick up where you left off.',
                      )}
                </Description>
              </>
            }
            asideBody={<DeadlinesContainer deadlines={deadlines} />}
            asideFooter={<Footer />}
          >
            {dashboardVm.isCompletelyEmpty ? (
              canCreateJob ? (
                <ServiceTypesContainer
                  vm={dashboardVm.serviceSelectionVm}
                  onStart={dashboardVm.handleServiceTypeStart}
                />
              ) : (
                <MissingJobCreatePermissionEmptyState />
              )
            ) : (
              <JobsContainer vm={dashboardVm} />
            )}
            {dashboardVm.isReturningCustomerFromPriorYear && (
              <ViewAllJobsTip to={links.jobs()} />
            )}
            {!isSpacious && <Separator />}
          </PageLayout>
        </Page>
      </PageHeader>

      <ServiceTypesDialogContainer vm={dashboardVm.serviceSelectionVm} />
      <LegendsDialogContainer vm={dashboardVm.serviceSelectionVm} />
      <ServiceTypeInfoDialogContainer vm={dashboardVm} />
      <CallAssistanceDialogsContainer vm={dashboardVm.callCenterAssistanceVm} />
      <StartNewServiceConfirmationDialogContainer
        vm={dashboardVm.serviceSelectionVm}
      />
    </PageTopOffsetContext>
  )
})

/**
 * Enables deep-linking to service selection dialogs by syncing URL with ViewModel state.
 *
 * The ViewModel drives valid state transitions based on business logic, while the URL
 * syncs to reflect the current state. This avoids the complexity of using the URL as
 * the source of truth for modal transitions.
 *
 * @param dashboardVm The dashboard view model containing service selection state
 */
function useSyncUrlWithSelectionState(dashboardVm: DashboardViewModel) {
  const selectionVm = dashboardVm.serviceSelectionVm
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const stateFromUrl$ = useSubject<ServiceSelectionState>(
    () => new BehaviorSubject(selectionVm.serviceSelectionState),
  )

  useLayoutEffect(() => {
    // Wait for ViewModel to be ready (legends loaded, etc.)
    const ready$ = toStream(() => dashboardVm.isReady, true).pipe(
      filter(Boolean),
    )

    // When URL changes, update ViewModel to match
    const syncToVm$ = stateFromUrl$.pipe(
      // Defer state restoration until VM is ready with all necessary data
      // (available service types, legends, etc.)
      switchMap((stateFromUrl) => ready$.pipe(map(() => stateFromUrl))),
      tap(selectionVm.fromServiceSelectionState),
      share(), // Share to prevent multiple subscriptions
    )

    // When ViewModel state changes, update URL to match
    const syncToUrl$ = toStream(
      () => selectionVm.serviceSelectionState,
      true,
    ).pipe(
      // Wait for initial URL state to be processed before syncing back
      // This prevents overwriting the URL on initial load
      withLatestFrom(syncToVm$),
      unpair(),
      tap((selection) => {
        const url = mapSelectionStateToURL(selection)
        void navigate(url, { replace: true, preventScrollReset: true })
      }),
    )

    const initialSync$ = syncToVm$.pipe(
      take(1),
      tap(() => dashboardVm.notifyUrlSyncReady()),
      ignoreElements(),
    )

    const subscription = merge(syncToVm$, syncToUrl$, initialSync$).subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [navigate, stateFromUrl$, selectionVm, dashboardVm])

  // Parse URL changes and emit new state
  useLayoutEffect(() => {
    const state = parseURLToSelectionState(pathname, searchParams)
    stateFromUrl$.next(state)
  }, [pathname, searchParams, selectionVm, stateFromUrl$])

  // Redirect to the draft editor when a draft has been created
  useObservable(
    selectionVm.draftCreated$,
    useEffectEvent(async (id: Id) => {
      // Set up a promise that waits for the URL state to indicate that
      // no dialogs are open.
      const stateChange = firstValueFrom(
        stateFromUrl$.pipe(filter((state) => !state.isShowingServiceSelection)),
      )

      // Force a navigation to the home screen, closing any of the
      // service selection dialogs, and ensures that if the user navigates
      // back from the draft editor that they land on the home directly,
      // rather than with dialogs open.
      void navigate(links.home(), { replace: true })

      // Wait for the URL to reflect that no service selection modal is
      // being displayed.
      await stateChange

      // Now redirect to the newly created draft
      void navigate(links.editDraftJob(id))
    }),
  )

  // Redirect to the existing in-progress job when the user picks
  // "Go to existing job" in the confirmation dialog.
  useObservable(
    selectionVm.goToExistingJob$,
    useEffectEvent((id: Id) => {
      void navigate(links.jobDetails(id))
    }),
  )
}

/**
 * Maps the current service selection state to a URL for deep-linking.
 */
function mapSelectionStateToURL(state: ServiceSelectionState) {
  if (!state.isShowingServiceSelection) {
    return links.home()
  }

  if (state.selectedServiceType) {
    return links.startNewJob(state.selectedServiceType)
  }

  return links.startNewService()
}

/**
 * Parses the current URL to determine the service selection state.
 */
function parseURLToSelectionState(
  pathname: string,
  params: URLSearchParams,
): ServiceSelectionState {
  const matchNewRoute = matchPath(links.startNewService(), pathname)
  if (!matchNewRoute) {
    return {
      isShowingServiceSelection: false,
      selectedServiceType: null,
    }
  }

  const serviceType = parseServiceTypeFromParams(params)

  return {
    isShowingServiceSelection: true,
    selectedServiceType: serviceType,
  }
}
